import { infiniteQueryOptions, keepPreviousData, queryOptions, useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData, type QueryClient } from '@tanstack/react-query'
import { api, unwrap } from '@/lib/api'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import type { DataEnvelope, LibraryRule, Paginated, RuleCatalogueEntry, RuleValidation } from '@/types/api'
import { canonicalJson } from './lib/model'

/**
 * Rule library and rule tooling (SIGNETIX_LOGIC.md §8):
 * `GET/POST /rules`, `GET/PATCH/DELETE /rules/{id}` (409 `rule-in-use`), `POST /rules/validate`,
 * `GET /rules/catalogue`.
 */

export interface RuleListParams {
  search?: string
}

export interface RuleInput {
  name: string
  description?: string | null
  rule: Record<string, unknown>
}

export const ruleKeys = {
  all: ['rules'] as const,
  lists: () => [...ruleKeys.all, 'list'] as const,
  list: (params: RuleListParams) => [...ruleKeys.lists(), params] as const,
  options: () => [...ruleKeys.lists(), 'options'] as const,
  detail: (id: string) => [...ruleKeys.all, 'detail', id] as const,
  catalogue: () => [...ruleKeys.all, 'catalogue'] as const,
  validate: (canonical: string) => [...ruleKeys.all, 'validate', canonical] as const,
}

/* ------------------------------------------------------------------ queries */

export function rulesInfiniteOptions(params: RuleListParams) {
  return infiniteQueryOptions({
    queryKey: ruleKeys.list(params),
    queryFn: ({ pageParam, signal }) => api.get<Paginated<LibraryRule>>('/rules', { signal, query: { search: params.search, cursor: pageParam, per_page: 50 } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.meta?.next_cursor ?? undefined,
  })
}

export function useRules(params: RuleListParams, enabled = true) {
  return useInfiniteQuery({ ...rulesInfiniteOptions(params), enabled })
}

/** Every saved rule (first 100) for pickers. */
export function useRuleOptions(enabled = true) {
  return useQuery({
    queryKey: ruleKeys.options(),
    queryFn: ({ signal }) => api.get<Paginated<LibraryRule>>('/rules', { signal, query: { per_page: 100 } }).then((r) => r.data),
    enabled,
    staleTime: 30_000,
  })
}

export function ruleOptions(id: string) {
  return queryOptions({
    queryKey: ruleKeys.detail(id),
    queryFn: ({ signal }) => unwrap(api.get<DataEnvelope<LibraryRule>>(`/rules/${id}`, { signal })),
  })
}

export function useRule(id: string | null | undefined) {
  return useQuery({ ...ruleOptions(id ?? ''), enabled: !!id })
}

/** References the builder can offer (variables, built-ins, data sources, engine refs). */
export function useRuleCatalogue(enabled = true) {
  return useQuery({
    queryKey: ruleKeys.catalogue(),
    queryFn: ({ signal }) => unwrap(api.get<DataEnvelope<RuleCatalogueEntry[]>>('/rules/catalogue', { signal })),
    enabled,
    staleTime: 60_000,
  })
}

/** Server validation of a (complete) rule, debounced; `null` rule = nothing to validate. */
export function useRuleValidation(rule: Record<string, unknown> | null, enabled = true) {
  const canonical = rule ? canonicalJson(rule) : ''
  const debounced = useDebouncedValue(canonical, 300)
  return useQuery({
    queryKey: ruleKeys.validate(debounced),
    queryFn: ({ signal }) => unwrap(api.post<DataEnvelope<RuleValidation>>('/rules/validate', { rule: JSON.parse(debounced) as unknown }, { signal })),
    enabled: enabled && !!debounced && debounced === canonical,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  })
}

/* ---------------------------------------------------------------- mutations */

function invalidateRuleConsumers(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: ruleKeys.lists() })
  // Conditions are shown (by name) on schedules, campaigns and channel items.
  void qc.invalidateQueries({ queryKey: ['schedules'] })
  void qc.invalidateQueries({ queryKey: ['campaigns'] })
  void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'channels' && q.queryKey[3] === 'items' })
}

export function useCreateRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: RuleInput) => unwrap(api.post<DataEnvelope<LibraryRule>>('/rules', input)),
    onSuccess: (r) => {
      qc.setQueryData(ruleKeys.detail(r.id), r)
      void qc.invalidateQueries({ queryKey: ruleKeys.lists() })
    },
  })
}

export function useUpdateRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<RuleInput> & { id: string }) => unwrap(api.patch<DataEnvelope<LibraryRule>>(`/rules/${id}`, input)),
    onSuccess: (r) => {
      qc.setQueryData(ruleKeys.detail(r.id), r)
      qc.setQueriesData<InfiniteData<Paginated<LibraryRule>>>({ queryKey: ruleKeys.lists() }, (data) =>
        data?.pages ? { ...data, pages: data.pages.map((p) => ({ ...p, data: p.data.map((x) => (x.id === r.id ? r : x)) })) } : data,
      )
      invalidateRuleConsumers(qc)
    },
  })
}

export function useDeleteRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/rules/${id}`),
    onSuccess: (_d, id) => {
      qc.removeQueries({ queryKey: ruleKeys.detail(id) })
      void qc.invalidateQueries({ queryKey: ruleKeys.lists() })
    },
  })
}

/** Total consumers of a library rule. */
export function usageCount(r: Pick<LibraryRule, 'usage'>): number {
  return (r.usage?.campaigns ?? 0) + (r.usage?.schedules ?? 0) + (r.usage?.channel_items ?? 0)
}

/** "2 schedules, 1 campaign" */
export function usageText(r: Pick<LibraryRule, 'usage'>): string {
  const parts: string[] = []
  const add = (n: number, one: string, many: string) => n > 0 && parts.push(`${n} ${n === 1 ? one : many}`)
  add(r.usage?.campaigns ?? 0, 'campaign', 'campaigns')
  add(r.usage?.schedules ?? 0, 'schedule', 'schedules')
  add(r.usage?.channel_items ?? 0, 'channel item', 'channel items')
  return parts.length ? parts.join(', ') : 'Not used'
}
